import { CSSProperties } from "react";
import { ColorName, Colors, Icon, IconName } from "main";

/********* Styles ********/
const styles = {
  container: ({
    isActive,
    isDisabled,
    activeBgColor,
    iconColor,
    label,
  }: {
    isActive: boolean | undefined;
    isDisabled: boolean | undefined;
    activeBgColor: ColorName;
    iconColor: ColorName;
    label: string | undefined;
  }) =>
    ({
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      background: isActive ? Colors[activeBgColor] : "transparent",
      padding: label ? "16px 8px" : 8,
      cursor: isDisabled ? "not-allowed" : "pointer",
      border: `2px solid ${isDisabled ? Colors.slate300 : isActive ? (iconColor === "black" ? Colors.blue500 : Colors[iconColor]) : Colors.black}`,
      borderRadius: 8,
      width: "100%",
    }) as CSSProperties,
  label: ({
    isDisabled,
    isSelected,
  }: {
    isDisabled: boolean | undefined;
    isSelected: boolean | undefined;
  }) =>
    ({
      margin: 0,
      fontSize: 14,
      lineHeight: "24px",
      textAlign: "center",
      color: isDisabled ? (isSelected ? Colors.black : Colors.slate300) : Colors.black,
    }) as CSSProperties,
};

/********* Types ********/
export type ChoiceButtonIcon = {
  name: IconName;
  color: ColorName;
};

type ChoiceButtonProps = {
  icon: ChoiceButtonIcon;
  isDisabled?: boolean;
  isSelected?: boolean;
  label?: string;
  onChange?: (value: string) => void;
  value: string;
  activeBgColor?: ColorName;
};

/********* Component ********/
export function ChoiceButton(props: ChoiceButtonProps) {
  const {
    icon,
    isDisabled,
    isSelected,
    label,
    onChange = () => {},
    value,
    activeBgColor = "white300",
  } = props;

  function getIconColor() {
    // TODO: no mock available for "disabled and isSelected"
    // This state is currently not being used by the app and is still in-progress by design team.
    if (isDisabled) return isSelected ? Colors.black : Colors.slate300;
    // If the button is selected and the icon color is black, override the color to blue500.
    return isSelected && icon.color === "black" ? Colors.blue500 : Colors[icon.color];
  }

  return (
    <button
      data-testid={`choice-button-${label}`}
      style={styles.container({
        isActive: isSelected,
        isDisabled,
        activeBgColor,
        iconColor: icon.color,
        label,
      })}
      aria-checked={isSelected}
      tabIndex={0}
      onClick={() => {
        if (!isDisabled && !isSelected) {
          onChange(value);
        }
      }}
    >
      <Icon name={icon.name} color={getIconColor()} />
      {label && <p style={styles.label({ isDisabled, isSelected })}>{label}</p>}
    </button>
  );
}
